/**
 * The alert groups' pure half — owner ruling DEC-101 (2026-10-01), `docs/adr/9040`.
 *
 * ~14,700 alerts came out of the first sweep, and nobody reviews them one by one. The inbox shows
 * ONE ROW PER ALERT TYPE (rule) in one value of a dimension the planner chooses — market (the
 * default), store or department — "one email per type". Opening a group lists its alerts; an act
 * takes the whole group, or its narrowed set, in one request (DEC-90), each alert still recording
 * its own event.
 *
 * **The URL is the scope** (CLAUDE.md §3.4). The inbox: `?view=groups|alerts`, `?by=`, `?queue=`,
 * `?state=`, `?overdue=`, `?rule=`, `?country=`. The group page: the group's FILTER — `?rule=&by=
 * &key=` (+ `?country=` for a department) — then the narrowing (`?store=`, `?department=`), the
 * sub-grouping (`?sub=`) and the alert open in the detail (`?alert=`). The filter is the group's
 * identity because it is what an act sends: the platform's opaque `groupKey` opens nothing (no read
 * takes it), and a link that reconstructs the set is what DEC-90 asks for.
 *
 * **No market is chosen for the planner** (DEC-85; ADR 9039 defaults runs only). `by=department`
 * with no `?country=` reads nothing and asks for a market.
 */

import type { AlertDecision, AlertState, FoundationsListExceptionsParams } from '@/api/generated';
import type { ApiProblemError } from '@/api';
import {
  GROUP_LIMIT, daysSince as sharedDaysSince, groupCurrentCount, groupMaximumCount, isGroupProblem,
} from '@/planning';

import {
  ALERT_GROUP_CHANGED, ALERT_GROUP_DIMENSIONS, ALERT_GROUP_TOO_LARGE, NO_VALUE,
  type AlertGroup, type AlertGroupAct, type AlertGroupActionParams, type AlertGroupDimension, type AlertGroupFilter,
  type AlertGroupQuery,
} from '@/api/pending/alert-groups';
import { foundationsListExceptions } from '@/api/generated';
import {
  ALERT_QUEUES, ALERT_STATES, DEPARTMENT_PARAM, OVERDUE_PARAM, QUEUE_PARAM, STATE_PARAM, type AlertQueueName,
} from './alert-queue';
import { COUNTRY_PARAM, readCountry } from './data-schedules';

export const VIEW_PARAM = 'view';
export const BY_PARAM = 'by';
export const RULE_PARAM = 'rule';
export const KEY_PARAM = 'key';
export const STORE_PARAM = 'store';
export const ARTICLE_PARAM = 'article';
export { DEPARTMENT_PARAM, NO_VALUE };
export const SUB_PARAM = 'sub';
export const ALERT_PARAM = 'alert';

/** The group page's path: the filter travels in the query (see the head of this file). */
export const ALERT_GROUP_PATH = '/foundations/exceptions/group';
export const ALERT_INBOX_PATH = '/foundations/exceptions';

/** Groups drawn: the top fifty (ADR 9034's bound; the grouped queue's, ADR 9042). */
export const ALERT_GROUP_LIMIT = GROUP_LIMIT;

/**
 * Groups asked per page when the inbox reads EVERY group (the read's maximum, `limit` ≤ 500). By
 * store a market has hundreds of groups — 413 on production, 2026-10-02 — and the inbox drew the
 * first fifty of them while its sentence said 413, with no way to the rest. It now follows
 * `nextCursor` to the end, ranks every group read (ADR 9034) and exports all of them.
 */
export const ALERT_GROUP_PAGE_SIZE = 500;

/** Pages the inbox follows at most (20,000 groups); past it the inbox says it stopped. */
export const ALERT_GROUP_MAX_PAGES = 40;

export type AlertView = 'groups' | 'alerts';

/**
 * Which view the inbox opens. `?view=` wins; with none, the grouped view once its read is pinned,
 * and the per-alert list until then — so production keeps the inbox it has today (ADR 9040 §4).
 */
export function readAlertView(search: string, groupsPinned: boolean): AlertView {
  const view = new URLSearchParams(search).get(VIEW_PARAM);
  if (view === 'groups' || view === 'alerts') return view;
  return groupsPinned ? 'groups' : 'alerts';
}

function param(params: URLSearchParams, name: string): string | null {
  const raw = params.get(name)?.trim() ?? '';
  return raw === '' ? null : raw;
}

function isDimension(value: string | null): value is AlertGroupDimension {
  return value !== null && (ALERT_GROUP_DIMENSIONS as readonly string[]).includes(value);
}

/** What every grouped read is filtered by. */
export interface AlertGroupFilters {
  /**
   * `all` when the link names none: the grouped inbox is where the whole sweep is triaged and
   * handed out, so it opens on every group in scope with its unassigned count beside it. The
   * per-alert list keeps opening on `mine` (ADR 2102). My work's links name their queue.
   */
  readonly queue: AlertQueueName;
  readonly state: AlertState;
  readonly overdueOnly: boolean;
}

export interface AlertGroupInboxScope extends AlertGroupFilters {
  readonly by: AlertGroupDimension;
  readonly ruleId: string | null;
  readonly country: string | null;
}

function readFilters(params: URLSearchParams): AlertGroupFilters {
  const queue = params.get(QUEUE_PARAM);
  const state = params.get(STATE_PARAM);
  return {
    queue: queue !== null && (ALERT_QUEUES as readonly string[]).includes(queue) ? queue as AlertQueueName : 'all',
    state: state !== null && (ALERT_STATES as readonly string[]).includes(state) ? state as AlertState : 'open',
    overdueOnly: params.get(OVERDUE_PARAM) === 'true',
  };
}

/** The inbox a link names. Pure. */
export function readAlertGroupInboxScope(search: string): AlertGroupInboxScope {
  const params = new URLSearchParams(search);
  const by = params.get(BY_PARAM);
  return {
    ...readFilters(params),
    by: isDimension(by) ? by : 'country',
    ruleId: param(params, RULE_PARAM),
    country: readCountry(search),
  };
}

/** A department grouping is one market's: without one, the inbox asks for it and reads nothing. */
export function inboxNeedsMarket(scope: Pick<AlertGroupInboxScope, 'by' | 'country'>): boolean {
  return scope.by === 'department' && scope.country === null;
}

function filterQuery(filters: AlertGroupFilters): Pick<AlertGroupQuery, 'queue' | 'state' | 'overdue' | 'open_only'> {
  return { queue: filters.queue, state: filters.state, open_only: false, ...(filters.overdueOnly ? { overdue: true } : {}) };
}

/** The groups read for the inbox, or `null` while a department grouping has no market. */
export function alertGroupInboxQuery(scope: AlertGroupInboxScope, limit = ALERT_GROUP_LIMIT, cursor?: string): AlertGroupQuery | null {
  if (inboxNeedsMarket(scope)) return null;
  return {
    group_by: scope.by,
    ...filterQuery(scope),
    ...(scope.ruleId === null ? {} : { rule_id: scope.ruleId }),
    ...(scope.country === null ? {} : { country_key: scope.country }),
    ...(cursor === undefined ? {} : { cursor }),
    limit,
  };
}

// ── the group page ───────────────────────────────────────────────────────────

/** How the group page sub-groups its alerts (`?sub=`). Department through the groups read. */
export const ALERT_SUB_GROUPINGS = ['store', 'department', 'article'] as const;
export type AlertSubGrouping = (typeof ALERT_SUB_GROUPINGS)[number];

export interface AlertGroupPageScope extends AlertGroupFilters {
  readonly ruleId: string;
  readonly by: AlertGroupDimension;
  readonly key: string;
  /** The market: the key itself for a market group; given for a department group; optional for a store. */
  readonly country: string | null;
  /** The narrowing inside the group, each a platform filter (`-`: no value), so an act carries it. */
  readonly store: string | null;
  readonly department: string | null;
  readonly article: string | null;
  readonly sub: AlertSubGrouping | null;
}

/** The group a link names, or `null` when it does not name one completely. Pure. */
export function readAlertGroupPageScope(search: string): AlertGroupPageScope | null {
  const params = new URLSearchParams(search);
  const ruleId = param(params, RULE_PARAM);
  const by = params.get(BY_PARAM);
  const key = param(params, KEY_PARAM);
  if (ruleId === null || key === null || !isDimension(by)) return null;
  const country = by === 'country' ? key.toUpperCase() : readCountry(search);
  if (by === 'department' && country === null) return null;
  const sub = params.get(SUB_PARAM);
  return {
    ...readFilters(params),
    ruleId,
    by,
    key,
    country,
    store: by === 'store' ? null : param(params, STORE_PARAM),
    department: by === 'department' ? null : param(params, DEPARTMENT_PARAM),
    article: param(params, ARTICLE_PARAM),
    sub: sub !== null && (ALERT_SUB_GROUPINGS as readonly string[]).includes(sub) ? sub as AlertSubGrouping : null,
  };
}

/** The sub-groupings a group offers: never its own dimension; department only with a market. */
export function subGroupingsFor(scope: Pick<AlertGroupPageScope, 'by' | 'country'>): readonly AlertSubGrouping[] {
  return ALERT_SUB_GROUPINGS.filter((sub) => sub !== scope.by && (sub !== 'department' || scope.country !== null));
}

/** Whether the page is narrowed inside its group. */
export function isNarrowed(scope: Pick<AlertGroupPageScope, 'store' | 'department' | 'article'>): boolean {
  return scope.store !== null || scope.department !== null || scope.article !== null;
}

/**
 * The set an act takes: the group's filter and, with `narrowed`, the narrowing on the link. Every
 * field is a platform filter, so the set is reconstructible (DEC-90).
 */
export function alertGroupFilter(scope: AlertGroupPageScope, narrowed = true): AlertGroupFilter {
  const store = scope.by === 'store' ? scope.key : narrowed ? scope.store : null;
  const department = scope.by === 'department' ? scope.key : narrowed ? scope.department : null;
  const article = narrowed ? scope.article : null;
  return {
    ruleId: scope.ruleId,
    ...(scope.country === null ? {} : { countryKey: scope.country }),
    ...(store === null ? {} : { storeKey: store }),
    ...(department === null ? {} : { departmentFamily: department }),
    ...(article === null ? {} : { articleKey: article }),
    queue: scope.queue,
    state: scope.state,
    ...(scope.overdueOnly ? { overdue: true } : {}),
  };
}

/**
 * Whether the pinned `GET /foundations/exceptions` takes `article_key` (bata-platform #496 adds it).
 * Until the re-pin it does not, and the article narrowing is applied to the rows read
 * (`matchesNarrowing`) — the same set, because the article filter means "exactly this article".
 */
export const ITEMS_READ_TAKES_ARTICLE = (foundationsListExceptions.queryParams as readonly string[]).includes('article_key');

/** The alerts of a filter, through the pinned `GET /foundations/exceptions`. */
export function alertItemsQuery(
  filter: AlertGroupFilter, takesArticle = ITEMS_READ_TAKES_ARTICLE,
): NonNullable<FoundationsListExceptionsParams['query']> & { readonly article_key?: string } {
  return {
    ...(takesArticle && filter.articleKey !== undefined ? { article_key: filter.articleKey } : {}),
    rule_id: filter.ruleId,
    open_only: false,
    ...(filter.queue === undefined ? {} : { queue: filter.queue }),
    ...(filter.state === undefined ? {} : { state: filter.state }),
    ...(filter.overdue === true ? { overdue: true } : {}),
    ...(filter.countryKey === undefined ? {} : { country_key: filter.countryKey }),
    ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
    ...(filter.departmentFamily === undefined ? {} : { department_family: filter.departmentFamily }),
  };
}

/** The groups read that answers with this group alone (its figures for the header). */
export function alertGroupReadQuery(scope: AlertGroupPageScope, groupBy: AlertGroupDimension = scope.by, narrowed = false): AlertGroupQuery {
  const filter = alertGroupFilter(scope, narrowed);
  return {
    group_by: groupBy,
    rule_id: filter.ruleId,
    ...filterQuery(scope),
    ...(filter.countryKey === undefined ? {} : { country_key: filter.countryKey }),
    ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
    ...(filter.departmentFamily === undefined ? {} : { department_family: filter.departmentFamily }),
    ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    limit: ALERT_GROUP_LIMIT,
  };
}

/** Whether a row read belongs to the narrowed set for what the items read could not filter itself. */
export function matchesNarrowing(
  alert: { readonly articleKey?: string | null }, filter: AlertGroupFilter, takesArticle = ITEMS_READ_TAKES_ARTICLE,
): boolean {
  if (takesArticle || filter.articleKey === undefined) return true;
  const article = alert.articleKey ?? null;
  return filter.articleKey === NO_VALUE ? article === null || article === '' : article === filter.articleKey;
}

/** The group among a page of groups that this scope names. */
export function findGroup(groups: readonly AlertGroup[], scope: Pick<AlertGroupPageScope, 'ruleId' | 'by' | 'key'>): AlertGroup | null {
  const same = (key: string | null): boolean => (scope.by === 'country'
    ? (key ?? '').toUpperCase() === scope.key.toUpperCase()
    : (key ?? NO_VALUE) === scope.key);
  return groups.find((group) => group.ruleId === scope.ruleId && group.dimension === scope.by && same(group.dimensionKey)) ?? null;
}

/**
 * The link that opens a group, carrying the inbox's filters. A store or department group with no
 * value (alerts with no store, say) is selected by `-` (ADR 2109) and opens like any other; the
 * MARKET's no-value bucket cannot be selected by the platform, so it is `null` — neither linkable nor
 * actable as a group; its alerts stay in the per-alert list.
 */
export function alertGroupHref(group: AlertGroup, filters: AlertGroupFilters): string | null {
  const raw = group.dimensionKey;
  const blank = raw === null || raw.trim() === '';
  if (blank && group.dimension === 'country') return null;
  const key = blank ? NO_VALUE : raw;
  const country = group.dimension === 'country' ? null : group.countryKey;
  if (group.dimension === 'department' && country === null) return null;
  const params = new URLSearchParams();
  params.set(RULE_PARAM, group.ruleId);
  params.set(BY_PARAM, group.dimension);
  params.set(KEY_PARAM, key);
  if (country !== null) params.set(COUNTRY_PARAM, country);
  params.set(QUEUE_PARAM, filters.queue);
  params.set(STATE_PARAM, filters.state);
  if (filters.overdueOnly) params.set(OVERDUE_PARAM, 'true');
  return `${ALERT_GROUP_PATH}?${params.toString()}`;
}

/** The act's set for a group row of the inbox — the same filter its page would send. */
export function inboxGroupFilter(group: AlertGroup, filters: AlertGroupFilters): AlertGroupFilter | null {
  const href = alertGroupHref(group, filters);
  if (href === null) return null;
  const scope = readAlertGroupPageScope(href.slice(href.indexOf('?')));
  return scope === null ? null : alertGroupFilter(scope, false);
}

/** Whole days between `iso` and `now`, never negative — the grouped queue's (ADR 9042). */
export const daysSince = sharedDaysSince;

/** One sub-group of a group's alerts, counted over the whole read. */
export interface AlertSubGroup {
  readonly key: string;
  readonly count: number;
  readonly overdue: number;
  readonly oldestRaisedAt: string;
}

/**
 * The alerts summarised by a key (store, article), most alerts first, ties by key. An alert with no
 * value for the key is counted under `null`'s own row, never dropped: the rows add up to the read.
 */
export function summariseAlerts<T extends { readonly raisedAt: string; readonly overdue?: boolean }>(
  alerts: readonly T[], keyOf: (alert: T) => string | null | undefined,
): readonly (AlertSubGroup & { readonly absent: boolean })[] {
  const byKey = new Map<string, { count: number; overdue: number; oldest: string; absent: boolean }>();
  for (const alert of alerts) {
    const raw = keyOf(alert);
    const absent = raw === null || raw === undefined || raw.trim() === '';
    const key = absent ? '' : raw;
    const entry = byKey.get(key) ?? { count: 0, overdue: 0, oldest: alert.raisedAt, absent };
    entry.count += 1;
    if (alert.overdue === true) entry.overdue += 1;
    if (alert.raisedAt < entry.oldest) entry.oldest = alert.raisedAt;
    byKey.set(key, entry);
  }
  return [...byKey.entries()]
    .map(([key, entry]) => ({ key, count: entry.count, overdue: entry.overdue, oldestRaisedAt: entry.oldest, absent: entry.absent }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

// ── the acts ─────────────────────────────────────────────────────────────────

export interface AlertGroupActInput {
  readonly action: AlertGroupAct;
  readonly filter: AlertGroupFilter;
  readonly expectedCount: number;
  readonly assignee?: string | null;
  readonly snoozedUntil?: string | null;
  readonly disposition?: AlertDecision | null;
  readonly reasonCode?: string | null;
  readonly commentary?: string;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The composed act, or `null` while something it needs is missing. Blank optionals are not sent. */
export function composeAlertGroupAction(input: AlertGroupActInput): AlertGroupActionParams | null {
  const { action, filter, expectedCount } = input;
  if (!Number.isInteger(expectedCount) || expectedCount <= 0) return null;
  const commentary = (input.commentary ?? '').trim();
  const note = commentary === '' ? {} : { commentary };
  const reason = (input.reasonCode ?? '').trim();
  switch (action) {
    case 'assign': {
      const assignee = (input.assignee ?? '').trim();
      if (assignee === '') return null;
      return { body: { action, filter, expectedCount, assignee, ...note } };
    }
    case 'unassign':
      return { body: { action, filter, expectedCount, ...note } };
    case 'snooze': {
      const until = input.snoozedUntil ?? '';
      if (!DAY.test(until) || reason === '') return null;
      return { body: { action, filter, expectedCount, snoozedUntil: until, reasonCode: reason, ...note } };
    }
    case 'dispose': {
      const disposition = input.disposition ?? null;
      if (disposition === null || reason === '') return null;
      return { body: { action, filter, expectedCount, disposition, reasonCode: reason, ...note } };
    }
    default:
      return null;
  }
}

/** Whether a write's refusal is "the set changed since you counted it" — the grouped queue's test (ADR 9042). */
export function isAlertGroupChanged(error: unknown): error is ApiProblemError {
  return isGroupProblem(error, 409, ALERT_GROUP_CHANGED);
}

/** The set is above the platform's bound (422 `alert_group_too_large`). */
export function isAlertGroupTooLarge(error: unknown): error is ApiProblemError {
  return isGroupProblem(error, 422, ALERT_GROUP_TOO_LARGE);
}

/** The current count the 409 (or the 422) states — `currentCount` — or `null` without one. */
export const changedCount = groupCurrentCount;

/** The bound a 422 `alert_group_too_large` states — `maximumCount` — or `null`. */
export const maximumCount = groupMaximumCount;
